import { call, put, all, select } from 'redux-saga/effects';
import * as Api from '@utils/Api';
import history from '../history';
import { types as signupsTypes } from '@reducers/signups';
import { Type as accountTypes } from '../reducers/Account';
import { popDest } from '@utils/intendedDest';
import { accountUserSelector } from '../selectors/Account';
import { setDestIfNeeded } from '@utils/intendedDest';
import { navigate } from '@reducers/Navigation';

const redirectToIntended = () => {
  const intended = popDest() || '/dash';
  history.push(intended);
};

export function* developerSignUp(action) {
  const {
    name,
    email,
    password,
    company,
    companyUrl,
    termsAccepted,
    plan,
    signUpCode,
    ref,
    benchmarksSignup,
  } = action;
  if (
    !email ||
    !password ||
    !name ||
    !company ||
    !companyUrl ||
    !termsAccepted
  ) {
    throw new Error('Missing required parameters for calling login API.');
  }
  const isAuthRequest = true;
  const response = yield call(
    Api.post,
    'signup',
    {
      name,
      email,
      password,
      company,
      company_url: companyUrl,
      plan,
      signup_code: signUpCode,
      ref,
      benchmarks_signup: benchmarksSignup,
    },
    isAuthRequest
  );
  if (!response.success) {
    yield put({
      type: signupsTypes.DEVELOPER_SIGNUP_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: signupsTypes.DEVELOPER_SIGNUP_SUCCESS,
    });
    redirectToIntended();
  }
}

export function* fetchRef(action) {
  const { ref } = action;

  if (!ref) {
    throw new Error('Missing required parameter for fetching referral data');
  }

  const response = yield call(Api.get, `ref/${ref}`);

  if (response.success) {
    yield put({
      type: signupsTypes.REF_SUCCESS,
      ref: response.data,
    });
  } else {
    yield put({
      type: signupsTypes.REF_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
      errorCode: response.status,
    });
  }
}

export function* fetchPromo(action) {
  const { signUpCode } = action;

  if (!signUpCode) {
    throw new Error(
      'Missing required parameter "signUpCode" for fetching promo data'
    );
  }
  const response = yield call(Api.get, `promo/${signUpCode}`);

  if (response.success) {
    yield put({
      type: signupsTypes.PROMO_SUCCESS,
      promo: response.data,
    });
  } else {
    yield put({
      type: signupsTypes.PROMO_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
      errorCode: response.status,
    });
  }
}

export function* fetchSkills() {
  const response = yield call(Api.get, 'skills');

  if (response.success) {
    yield put({
      type: signupsTypes.SKILLS_SUCCESS,
      skills: response.data,
    });
  } else {
    yield put({
      type: signupsTypes.SKILLS_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
      errorCode: response.status,
    });
  }
}

export function* reviewerSignUp(action) {
  const { name, email, password, phone } = action;
  if (!name || !email || !password) {
    throw new Error('Missing required parameters for reviewer signup API.');
  }

  const response = yield call(Api.post, 'signup/reviewer/account', {
    name,
    email,
    password,
    phone,
  });
  if (!response.success) {
    yield put({
      type: signupsTypes.REVIEWER_SIGNUP_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: signupsTypes.REVIEWER_SIGNUP_SUCCESS,
    });
    yield put({ type: accountTypes.FETCH_ACCOUNT_REQUEST });
  }
}

export function* updateReviewerProfile(action) {
  const { github, linkedin, resume, additionalLinks } = action;
  if (!github || !linkedin) {
    throw new Error('Missing required parameters for reviewer profile API.');
  }

  let response;
  if (resume) {
    response = yield call(
      Api.uploadWithParams,
      'signup/reviewer/profile',
      'resume',
      resume,
      {
        github_username: github,
        linked_in_url: linkedin,
        additional_links: additionalLinks,
      }
    );
  } else {
    response = yield call(Api.post, 'signup/reviewer/profile', {
      github_username: github,
      linked_in_url: linkedin,
      additional_links: additionalLinks,
    });
  }

  if (!response.success) {
    yield put({
      type: signupsTypes.UPDATE_REVIEWER_PROFILE_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: signupsTypes.UPDATE_REVIEWER_PROFILE_SUCCESS,
    });
    yield put({
      type: accountTypes.FETCH_ACCOUNT_REQUEST,
    });
  }
}

export function* addReviewerInfo(action) {
  const { hourlyRate, yearsDevExperience, yearsCodeReview, reviewDescription } =
    action;
  if (!hourlyRate || !yearsDevExperience || !yearsCodeReview) {
    throw new Error('Missing required parameters for additional-info API.');
  }

  const response = yield call(Api.post, 'signup/reviewer/additional-info', {
    hourly_rate: hourlyRate,
    years_developing: yearsDevExperience,
    years_reviewing: yearsCodeReview,
    review_description: reviewDescription,
  });

  if (!response.success) {
    yield put({
      type: signupsTypes.ADD_REVIEWER_INFO_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
    });
  } else {
    yield all([
      put({
        type: signupsTypes.ADD_REVIEWER_INFO_SUCCESS,
      }),
      put({ type: accountTypes.FETCH_ACCOUNT_REQUEST }),
    ]);
  }
}

export function* storeReviewerSkills(action) {
  const { skills, complete } = action;
  const response = yield call(Api.post, 'signup/reviewer/skills', { skills });
  if (!response.success) {
    yield put({
      type: signupsTypes.STORE_SKILLS_FAILURE,
      error: response.data ? response.data.error_message : 'Unknown error',
    });
  } else {
    yield put({
      type: signupsTypes.STORE_SKILLS_SUCCESS,
    });
    if (complete) {
      yield put({
        type: signupsTypes.COMPLETE_REVIEWER_SIGNUP_REQUEST,
      });
    } else {
      yield put({
        type: accountTypes.FETCH_ACCOUNT_REQUEST,
      });
    }
  }
}

export function* getReviewerSlackUrl(action) {
  const { callback } = action;
  const response = yield call(Api.get, 'signup/slack-url');
  if (callback) {
    const data = response.data ? response.data : {};
    const errorMessage = data.error_message
      ? data.error_message
      : 'Unable to get Slack URL';
    callback(data.slack_url, response.success ? null : errorMessage);
  }
}

export function* completeReviewerSignUp() {
  const response = yield call(Api.post, 'signup/reviewer/complete');
  if (!response.success) {
    yield put({
      type: signupsTypes.COMPLETE_REVIEWER_SIGNUP_FAILURE,
      error: response.data?.error_message || 'Unknown error',
    });
  } else {
    yield put({
      type: signupsTypes.COMPLETE_REVIEWER_SIGNUP_SUCCESS,
    });
    yield put({
      type: accountTypes.FETCH_ACCOUNT_REQUEST,
    });
  }
}

export function* acceptReviewerTerms() {
  const response = yield call(Api.post, 'signup/reviewer/init');
  if (!response.success) {
    yield put({
      type: signupsTypes.REVIEWER_TERMS_FAILURE,
      error: response.data?.error_message || 'Unknown error',
    });
  } else {
    yield put({
      type: signupsTypes.REVIEWER_TERMS_SUCCESS,
    });
    yield put({
      type: accountTypes.FETCH_ACCOUNT_REQUEST,
    });
  }
}

export function* tryRedirectToVerificationPage(pathname) {
  const user = yield select(accountUserSelector);

  if (!user.email_verified) {
    const { is_reviewer: isReviewer } = user;
    if (isReviewer) {
      if (!pathname.startsWith('/signups/reviewer/verification')) {
        yield call(setDestIfNeeded);
        yield put(navigate({ href: '/signups/reviewer/verification' }));
      }
      return true;
    }

    if (!isReviewer) {
      if (!pathname.startsWith('/verification')) {
        yield call(setDestIfNeeded);
        yield put(navigate({ href: '/verification' }));
      }
      return true;
    }
  }
  return false;
}

export function* tryRedirectToReviewerSignup(pathname) {
  const user = yield select(accountUserSelector);

  if (!user.is_reviewer) {
    return false;
  }
  const reviewerProfileComplete = Boolean(
    user.reviewer_profile?.completed_signup_at
  );
  // user hasn't onboarded as a reviewer and hasn't completed reviewer profile
  if (!user.roles?.includes('reviewer') && !reviewerProfileComplete) {
    if (pathname.startsWith('/signups/reviewer/profile')) {
      return true;
    }
    // Navigate to reviewer signup
    yield put(navigate({ href: '/signups/reviewer/profile' }));
    return true;
  }
  // user is not also a developer, or is a reviewer on the signups page; send to reviewer dash
  if (
    !pathname.startsWith('dash/user/settings') &&
    (!user.is_developer || pathname.startsWith('/signups/reviewer'))
  ) {
    yield put(navigate({ href: '/reviewer' }));
    return true;
  }
}
